import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, Link } from "react-router-dom";
import api from "../api/api";
import Header from "../components/Header";
import LeftSidebar from "../components/sidebar";
/* ---------- Helpers ---------- */
const parseFlightSegments = (description = '') => {
  const lines = description.split('\n').map(l => l.trim()).filter(Boolean);

  const days = [];
  let currentDay = null;

  lines.forEach((line) => {

    const rptMatch = line.match(/^Rpt-\s*(\d+)\s*(\d{2}[A-Z]{3})/);

    if (rptMatch) {
      const rawReport = rptMatch[1];
      const formattedReport =
        rawReport.length === 4
          ? `${rawReport.slice(0, 2)}:${rawReport.slice(2)}`
          : rawReport;

      const rawDay = rptMatch[2];
      const formattedDay =
        rawDay.length === 5
          ? `${rawDay.slice(0, 2)} ${rawDay.slice(2)}`
          : rawDay;

      currentDay = {
        reportTime: formattedReport,
        day: formattedDay,
        flights: [],
        layover: null,
        hotel: null,
      };

      days.push(currentDay);
      return;
    }

    const flightMatch = line.match(
      /^(DL\d+)\s+([A-Z]{3})-([A-Z]{3})\s+(\d{2}:\d{2})-(\d{2}:\d{2})\s+(\w+)/
    );

    if (flightMatch && currentDay) {
      currentDay.flights.push({
        flightNo: flightMatch[1],
        from: flightMatch[2],
        to: flightMatch[3],
        departure: flightMatch[4],
        arrival: flightMatch[5],
        aircraft: flightMatch[6],
      });
      return;
    }

    const layoverMatch = line.match(/^LAYOVER\s+(\d{2}:\d{2})\/([A-Z]{3})/);

    if (layoverMatch && currentDay) {
      currentDay.layover = {
        duration: layoverMatch[1],
        location: layoverMatch[2],
      };
      return;
    }

    const hotelMatch = line.match(/^(.+?):\s+(\d{3}-\d{3}-\d{4})/);

    if (hotelMatch && currentDay) {
      currentDay.hotel = {
        name: hotelMatch[1],
        phone: hotelMatch[2],
      };
    }

  });

  return days;
};

const extractCities = (eventData) => {
  if (!eventData?.description) return "--";

  const segments = parseFlightSegments(eventData.description);

  const airports = new Set();

  segments.forEach(day => {
    day.flights.forEach(f => {
      airports.add(f.from);
      airports.add(f.to);
    });

    if (day.layover?.location) {
      airports.add(day.layover.location);
    }
  });

  return airports.size ? [...airports].join(", ") : "--";
};

const calculateDutyDays = (start, end) => {
  if (!start || !end) return 0;

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return 0;
  }

  const oneDay = 24 * 60 * 60 * 1000;
  return Math.floor((endDate - startDate) / oneDay) + 1;
};

const getDetailRoute = (event) => {
  return `/rotation/${event.uid}`;
};

/* ---------- Main Component ---------- */
const EventListView = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("rotation_reports");
  /* Date filter */
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [tempStartDate, setTempStartDate] = useState("");
	const [tempEndDate, setTempEndDate] = useState("");



  /* Add Event */
  const [showAddModal, setShowAddModal] = useState(false);
  const [professionalDetails, setProfessionalDetails] = useState(null);

  /* Manual */
  const [manualForm, setManualForm] = useState({
    summary: "",
    start: "",
    end: "",
    location: "",
    description: "",
  });
  const [manualSaving, setManualSaving] = useState(false);
  const [manualFile, setManualFile] = useState(null);
  const [manualUploading, setManualUploading] = useState(false);

  /* Pagination */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  /* ---------- API ---------- */
  const fetchEvents = async (page = 1, type = filterType) => {
  setLoading(true);
  try {
    const res = await api.get("/event/dashboard/events", {
      params: {
        page,
        per_page: itemsPerPage,
        type,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      },
    });

    setEvents(res.data || {});
    setCurrentPage(page);
  } catch {
    toast.error("Failed to load events");
  } finally {
    setLoading(false);
  }
};


  const fetchProfessionalDetails = async () => {
    try {
      const res = await api.get("/user/professional-details");
      setProfessionalDetails(res.data);
    } catch {}
  };

 useEffect(() => {
  fetchEvents(1, filterType);
}, [filterType, startDate, endDate]);
useEffect(() => {
  fetchProfessionalDetails();
}, []);


  /* ---------- Remove ---------- */
  const handleRemove = async (uid) => {
    if (!window.confirm("Remove this event?")) return;
    try {
      await api.delete(`/event/${uid}`);
      toast.success("Event removed");
      fetchEvents(currentPage, filterType);
    } catch {
      toast.error("Failed to remove event");
    }
  };

  /* ---------- Provider ---------- */
  const getProviderType = () => {
    const name =
      professionalDetails?.selected?.provider_type ||
      professionalDetails?.calendar_name ||
      "";
    const n = name.toLowerCase();
    if (n.includes("google")) return "google";
    if (n.includes("microsoft")) return "microsoft";
    if (n.includes("manual")) return "manual";
    return null;
  };

  const providerType = getProviderType();

  /* ---------- Manual Create ---------- */
  const handleManualCreate = async () => {
    if (!manualForm.summary) return toast.warn("Title required");

    try {
      setManualSaving(true);
      await api.post("/event/events", {
        ...manualForm,
        is_manual: true,
      });
      toast.success("Event added");
      fetchEvents();
      setShowAddModal(false);
    } catch {
      toast.error("Failed to add event");
    } finally {
      setManualSaving(false);
    }
  };

  /* ---------- Manual Upload ---------- */
  const handleManualUpload = async () => {
    if (!manualFile) return toast.warn("Choose .ics file");
    try {
      setManualUploading(true);
      const fd = new FormData();
      fd.append("file", manualFile);
      await api.post("/event/upload", fd);
      toast.success("File uploaded");
      fetchEvents();
      setShowAddModal(false);
    } catch {
      toast.error("Upload failed");
    } finally {
      setManualUploading(false);
    }
  };

  /* ---------- Filter Bucket ---------- */
  const bucket =
    filterType === "today"
      ? events.today_events
      : filterType === "upcoming"
      ? events.upcoming_events
      : filterType === "previous"
      ? events.previous_events
      : events.all_events;

  const displayEvents = bucket?.events || [];
  const totalPages = bucket?.total_pages || 1;

  if (loading) return <p className="text-center mt-5">Loading…</p>;

  return (
    <main className="dashboard-screen">
      <div className="container-fluid">
        <div className="row gx-5">
          <div className="col-lg-3 left-sidebar-main">
            <LeftSidebar />
          </div>

          <div className="col-lg-9">
            <Header />
            <div className="event-title-sec mb-4">
              <div className="event_inner">
                <h1>Event List</h1> 
<div className="filter_btn">
  <input
    type="date"
    className="form-control"
    value={tempStartDate}
    onChange={(e) => setTempStartDate(e.target.value)}
  />

  <input
    type="date"
    className="form-control"
    value={tempEndDate}
    onChange={(e) => setTempEndDate(e.target.value)}
  />
  <div className="filter_btn_inner">
    <button
      className="btn-apply"
      onClick={() => {
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
      }}
    >
      Apply
    </button>
    <button
      className="btn-clear ms-2"
      onClick={() => {
        setTempStartDate("");
        setTempEndDate("");
        setStartDate("");
        setEndDate("");
      }}
    >
      Clear
    </button>
  </div>
</div>

              </div>
              {providerType === "manual" && (
  <button className="btn5" onClick={() => setShowAddModal(true)}>
    <i className="bi bi-plus"></i> Add Event
  </button>
)}

            </div>

            <EventCards
              events={displayEvents}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(p) => fetchEvents(p, filterType)}
              onRemove={handleRemove}
              navigate={navigate}
            />
          </div>
        </div>
      </div>

      {/* ---------- ADD EVENT MODAL ---------- */}
      {showAddModal && (
        <div className="modal add-event-modal fade show d-block bg-dark bg-opacity-50">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5><img src="images/add-event.svg" className="img-fluid"/> Add Event</h5>
                <button className="btn-close" onClick={() => setShowAddModal(false)} />
              </div>

              <div className="modal-body">
                {!providerType && (
                  <div className="alert alert-warning">
                    Configure calendar first
                  </div>
                )}

                {providerType === "manual" && (
                  <>
                    

                    <input
                      type="file"
                      className="form-control mb-2"  accept=".ics"
                      onChange={(e) => setManualFile(e.target.files[0])}
                    />
                    <button
                      className="btn5 mt-3"
                      onClick={handleManualUpload}
                      disabled={manualUploading}
                    >
                      Upload ICS
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

/* ---------- Event Cards ---------- */
const EventCards = ({
  events,
  currentPage,
  totalPages,
  onPageChange,
  onRemove,
  navigate,
}) => {
  if (!events.length) return <p>No events found</p>;

  const formatDateTime = (datetime, timezone) => {
    if (!datetime) return "N/A";

    try {
      return new Date(datetime).toLocaleString("en-US", {
        timeZone: timezone || "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      }).replace(",", "");
    } catch {
      return datetime;
    }
  };

  return (
    <>
      {events.map((ev) => {
		  

        // ✅ SAME LOGIC AS DETAIL PAGE
        const flightSegments = ev?.description
          ? parseFlightSegments(ev.description)
          : [];

        const cities = ev.cities || extractCities(ev);
        const dutyDays = ev.duty_days || calculateDutyDays(ev.start, ev.end);
        const overnights = (ev.overnights !== undefined && ev.overnights !== null)
          ? ev.overnights
          : flightSegments.filter(day => day.layover).length;

        return (
          <div key={ev.id} className="dash-section mb-4">
            <div className="d-flex justify-content-between">
              <h5>{ev.summary}</h5>
              <div className="dropdown three-dot-menu">
                <button className="btn btn-link" data-bs-toggle="dropdown">
                  <i className="bi bi-three-dots-vertical" />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => onRemove(ev.uid)}
                    >
                      Remove
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="event-content">
                  Start:{" "}
                  <span>
                    {ev.event_type == 'rotation' ? formatDateTime(ev.report_datetime) : formatDateTime(ev.start, ev.start_timezone)} ({ev.start_timezone})
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <div className="event-content">
                  End:{" "}
                  <span>
                    {ev.event_type == 'rotation' ? formatDateTime(ev.release_datetime) : formatDateTime(ev.end, ev.end_timezone)} ({ev.end_timezone})
                  </span>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="event-content">
                  Cities: <span>{cities}</span>
                </div>
              </div>

              <div className="col-md-3">
                <div className="event-content">
                  Duty Days: <span>{dutyDays}</span>
                </div>
              </div>

              <div className="col-md-3">
                <div className="event-content">
                  Overnights: <span>{overnights}</span>
                </div>
              </div>

              <div className="col-md-12">
                <Link to={getDetailRoute(ev)} className="view-btn">
                  View
                </Link>
              </div>
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-controls d-flex justify-content-end align-items-center gap-3 mt-4">
          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>

          <div className="d-flex gap-2">
            {(() => {
              let startPage = Math.max(1, currentPage - 1);
              let endPage = Math.min(totalPages, startPage + 1);

              if (endPage - startPage < 1) {
                startPage = Math.max(1, endPage - 1);
              }

              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
              }

              return pages.map((page) => (
                <button
                  key={page}
                  className={`btn btn-sm page-btn ${
                    page === currentPage ? "active" : ""
                  }`}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              ));
            })()}
          </div>

          <button 
            className="btn btn-outline-secondary btn-sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
};

export default EventListView;
